import React, { useState } from 'react';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { useTranslation } from 'react-i18next';

import StoryButton from '../../../components/story-button/StoryButton';
import StoryCheckbox from '../../../components/story-checkbox/StoryCheckbox';
import GroupBadge from './components/group-badge/GroupBadge';
import RatioBadge from './components/ratio-badge/RatioBadge';
import ContentPreview from './components/content-preview/ContentPreview';
import EmptyPreview from './components/empty-preview/EmptyPreview';
import EditablePreview from './components/editable-preview/EditablePreview';
import PreviewTextSkeleton from './components/preview-text-skeleton/PreviewTextSkeleton';
import MatchedContentModal from '../../../components/matched-content-modal/MatchedContentModal';
import { STORY_GENERATED_CONTENT_STATUS, STORY_GENERATED_CONTENT_SUBTYPE } from '../../../../../constants';
import CommonCarousel from '../../../../../components/common-carousel/CommonCarousel';
import callApi from '../../../../../service/callApi';
import sendRequest from '../../../../../service/sendRequest';
import downloadFile from '../../../../../utils/downloadFile';
import { useProject } from '../../../../../context/ProjectContext';

import scss from './ContentCard.module.scss';

function ContentCard({ bucketId, contents = [], preview, previewStatus, isSelected, onSelect }) {
  const { t } = useTranslation('story');
  const { actions } = useProject();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isMatchedModalOpen, setIsMatchedModalOpen] = useState(false);
  const [matchedItems, setMatchedItems] = useState([]);
  const [matchedContext, setMatchedContext] = useState(null);
  const [isApplyingMatch, setIsApplyingMatch] = useState(false);
  const [isContinuingRegenerate, setIsContinuingRegenerate] = useState(false);

  const safeIndex = contents.length ? Math.min(activeIndex, contents.length - 1) : 0;
  const activeContent = contents[safeIndex];
  const contentIds = contents.map((content) => content.id);

  const isPreviewPending = previewStatus === STORY_GENERATED_CONTENT_STATUS.PENDING;
  const isActivePending = activeContent?.contentStatus === STORY_GENERATED_CONTENT_STATUS.PENDING;
  const isActiveVideo = activeContent?.subtype === STORY_GENERATED_CONTENT_SUBTYPE.MP4;
  const canDownload = Boolean(activeContent?.id) && !isActivePending && !isActiveVideo;
  const canRegenerate = Boolean(activeContent?.id) && !isActivePending && !isRegenerating && !isMatchedModalOpen;

  const handleSelect = () => {
    onSelect(contentIds);
  };

  const handleDownload = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!activeContent?.id || isDownloading) return;

    setIsDownloading(true);

    try {
      const response = await sendRequest(() =>
        callApi.get(`/api/story/download/generated?id=${activeContent.id}`, {
          responseType: 'arraybuffer',
        }),
      );

      const fileName =
        response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `${activeContent.id}.png`;
      const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));

      downloadFile(fileUrl, fileName);
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } finally {
      setIsDownloading(false);
    }
  };

  const closeMatchedModal = () => {
    setIsMatchedModalOpen(false);
    setMatchedItems([]);
    setMatchedContext(null);
  };

  const handleRegenerate = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!activeContent?.id || isRegenerating || isActivePending) return;

    setIsRegenerating(true);

    try {
      const matched = await actions('getMatchedStoryContent', { generatedContentId: activeContent.id }).unwrap();

      if (!matched.length) {
        await actions('reGenerateStoryContent', { generatedContentId: activeContent.id }).unwrap();
        return;
      }

      setMatchedContext({
        generatedContentId: activeContent.id,
        group: activeContent.group,
        ratio: activeContent.ratio,
        subtype: activeContent.subtype,
      });
      setMatchedItems(matched);
      setIsMatchedModalOpen(true);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCloseMatchedModal = () => {
    if (isApplyingMatch || isContinuingRegenerate) return;

    closeMatchedModal();
  };

  const handleUseMatchedVersion = async (newId) => {
    if (!matchedContext?.generatedContentId || isApplyingMatch || isContinuingRegenerate) return;

    setIsApplyingMatch(true);

    try {
      await actions('setStoryContent', {
        generatedContentId: matchedContext.generatedContentId,
        newId,
      }).unwrap();
      closeMatchedModal();
    } finally {
      setIsApplyingMatch(false);
    }
  };

  const handleContinueRegenerate = async () => {
    if (!matchedContext?.generatedContentId || isApplyingMatch || isContinuingRegenerate) return;

    setIsContinuingRegenerate(true);

    try {
      await actions('reGenerateStoryContent', { generatedContentId: matchedContext.generatedContentId }).unwrap();
      closeMatchedModal();
    } finally {
      setIsContinuingRegenerate(false);
    }
  };

  const handleBeforeChange = (_current, next) => {
    setActiveIndex(next);
  };

  const renderMedia = () => {
    if (!contents.length) {
      return <EmptyPreview />;
    }

    if (contents.length === 1) {
      const content = contents[0];

      return (
        <ContentPreview
          key={content.link}
          subtype={content.subtype}
          link={content.link}
          isPending={content.contentStatus === STORY_GENERATED_CONTENT_STATUS.PENDING}
        />
      );
    }

    return (
      <div className={scss.carousel}>
        <CommonCarousel onBeforeChange={handleBeforeChange} isLazyLoad initialSlide={safeIndex}>
          {contents.map((content) => (
            <div key={content.id} className={scss.slide}>
              <ContentPreview
                key={content.link}
                subtype={content.subtype}
                link={content.link}
                isPending={content.contentStatus === STORY_GENERATED_CONTENT_STATUS.PENDING}
              />
            </div>
          ))}
        </CommonCarousel>
      </div>
    );
  };

  return (
    <div className={scss.card}>
      <div className={scss.media}>
        <div className={scss.header}>
          <div className={`${scss.headerContent} ${scss.headerContentLeft}`}>
            {activeContent ? (
              <>
                <GroupBadge
                  group={activeContent.group}
                  displayValue={activeContent.displayValue}
                  ratio={activeContent.ratio}
                />
                <RatioBadge ratio={activeContent.ratio} />
              </>
            ) : null}
          </div>

          <div className={`${scss.headerContent} ${scss.headerContentRight}`}>
            {canRegenerate ? (
              <StoryButton
                icon={AutorenewOutlinedIcon}
                onClick={handleRegenerate}
                ariaLabel={t('regenerate')}
                disabled={isRegenerating}
              />
            ) : null}
            {canDownload ? (
              <StoryButton icon={DownloadOutlinedIcon} onClick={handleDownload} ariaLabel='download' />
            ) : null}
            {/* {activeContent ? <ContentStatus status={activeContent.contentStatus} /> : null} */}
            <StoryCheckbox checked={isSelected} onChange={handleSelect} />
          </div>
        </div>

        {renderMedia()}
      </div>

      {isPreviewPending ? <PreviewTextSkeleton /> : <EditablePreview contentId={bucketId} preview={preview} />}

      {isMatchedModalOpen ? (
        <MatchedContentModal
          isOpen={isMatchedModalOpen}
          items={matchedItems}
          group={matchedContext?.group}
          ratio={matchedContext?.ratio}
          subtype={matchedContext?.subtype}
          isApplying={isApplyingMatch}
          isRegenerating={isContinuingRegenerate}
          onClose={handleCloseMatchedModal}
          onUse={handleUseMatchedVersion}
          onRegenerate={handleContinueRegenerate}
        />
      ) : null}
    </div>
  );
}

export default ContentCard;
